export default class PriceUtils{ 

    private locale: string;
    private currency: string;
    
    constructor(locale: string = 'it-IT', currency: string = 'EUR'){
        
        this.locale = locale;
        this.currency = currency;

    }

    /**
     * Removes currency symbols, common invisible spaces and trims the string.
     * Accepts things like "&nbsp;", NBSP (\u00A0), narrow NBSP (\u202F), "EUR", "€".
     */
    sanitize(raw: string): string {

        if(!raw) return '';

        return String(raw)
        .replace(/&nbsp;|\u00A0|\u202F/g, ' ') // NBSPs to normal space
        .replace(/[€]/gi, '')
        .replace(/\bEUR\b/gi, '')
            .trim();
        
    }

    /**
     * Normalizes a price string to dot-decimal with fixed precision.
     * Examples:
     *  "15,00"         -> "15.00"
     *  "150,00"        -> "150.00"
     *  "1.234,56"      -> "1234.56"
     *  "1 234,56 €"    -> "1234.56"
     *  "1,234.56"      -> "1234.56"
     * Notes:
     *  - If both comma and dot appear, the right-most one is treated as the decimal separator.
     *  - Thousand groupers like spaces or apostrophes are removed.
     */
    normalizeToDotDecimal(raw: string | undefined, precision: number = 2): number {

        if (!raw) return 0;

        let s = this.sanitize(raw);

        // Handle negative sign (also support Unicode minus)
        const isNegative = /[-\u2212]/.test(s);
        s = s.replace(/[-\u2212]/g, '');

        // Remove common thousand groupers (spaces and apostrophes)
        const stripGroupers = (str: string) => str.replace(/[\s'’]/g, '');

        const lastComma = s.lastIndexOf(',');
        const lastDot = s.lastIndexOf('.');

        if(lastComma > -1 && lastDot > -1){
            
            // Both present -> the right-most is the decimal separator
            const decimalSep = lastComma > lastDot ? ',' : '.';

            if(decimalSep === ','){
                
                s = stripGroupers(s).replace(/\./g, ''); // remove thousands dots
                s = s.replace(/,/g, '.');
                // comma -> dot
            }else{
                
                s = stripGroupers(s).replace(/,/g, '');  // remove thousands commas
                // decimal dot stays as-is

            }

        }else if(lastComma > -1){
            
            // Only comma -> EU decimal
            s = stripGroupers(s).replace(/,/g, '.');
            
        }else if(lastDot > -1){
            
            // Only dot -> assume decimal dot
            s = stripGroupers(s);

        }else{
            
            // No separators -> keep digits only
            s = s.replace(/\D+/g, '');

        }

        const n = Number(s || '0');
        const val = Number.isFinite(n) ? n : 0;
        const signed = isNegative ? -val : val;
        
        return parseFloat(signed.toFixed(precision)); // return as number
        
    }


    /**
     * Converts a price string to integer cents.
     * "15,00" -> 1500 ; "150,00" -> 15000 ; "1.234,56" -> 123456
     */
    toCents(raw: string): number {

        const dot = this.normalizeToDotDecimal(raw, 2);
        const n = Number(dot);
        return Number.isFinite(n) ? Math.round(n * 100) : 0;

    }

    /**
     * Formats integer cents to a localized currency string (defaults to EUR, it-IT).
     * 123456 -> "1.234,56 €"
     */
    formatCents(cents: number): string {

        const val = (cents || 0) / 100;
        return new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(val);

    }

}